import { useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { ReduxState } from '@/redux/modules/types';
import { Dispatch } from '@/redux';
import { setTaxAnalysisComparisonItems } from '@/redux/modules/comparison';
import ComparisonItem from '@/types/comparison-item';

export const useComparisonItemsSelector = (onEditItem?: (item: ComparisonItem) => void) => {
  const dispatch = useDispatch<Dispatch>();
  const activeComparison = useSelector((state: ReduxState) => state.comparison.activeComparison);
  const originalComparisonItemId = activeComparison?.taxAnalysis?.originalComparisonItemId;
  const targetComparisonItemId = activeComparison?.taxAnalysis?.targetComparisonItemId;

  const originalItem = useMemo(
    () => (originalComparisonItemId ? activeComparison?.items.find(item => item._id === originalComparisonItemId) : undefined),
    [originalComparisonItemId, activeComparison?.items],
  );

  const targetItem = useMemo(
    () => (targetComparisonItemId ? activeComparison?.items.find(item => item._id === targetComparisonItemId) : undefined),
    [targetComparisonItemId, activeComparison?.items],
  );

  const handleRemoveItem = useCallback(
    (isOriginal: boolean) => {
      if (isOriginal) {
        // If removing original, clear everything
        dispatch(setTaxAnalysisComparisonItems(undefined, undefined));
      } else {
        // If target item is removed, keep original and just clear target
        dispatch(setTaxAnalysisComparisonItems(originalItem?._id, undefined));
      }
    },
    [dispatch, originalItem?._id],
  );

  const handleSelectItem = useCallback(
    (item: ComparisonItem, isOriginal: boolean) => {
      const newOriginalId = isOriginal ? item._id : originalItem?._id;
      const newTargetId = isOriginal ? targetItem?._id : item._id;

      // Defer the dispatch to avoid updating during render
      queueMicrotask(() => {
        dispatch(setTaxAnalysisComparisonItems(newOriginalId, newTargetId));
      });
    },
    [dispatch, originalItem?._id, targetItem?._id],
  );

  const handleEditItem = useCallback(
    (isOriginal: boolean) => {
      const item = isOriginal ? originalItem : targetItem;
      if (onEditItem && item) {
        onEditItem(item);
      }
    },
    [onEditItem, originalItem, targetItem],
  );

  return {
    originalItem,
    targetItem,
    handleRemoveItem,
    handleSelectItem,
    handleEditItem,
  };
};
